import { firstValueFrom } from 'rxjs';
import { TemplateControl } from '../request-result-modal.types';
import { FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { UserService } from '@6grain/masofa-monolith-api-client/identity';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { Crop } from '@6grain/masofa-monolith-api-client/dictionaries';
import { userNames } from '@app/utils/_msf';

/**
 * Service class for enriching template controls with additional data
 */
export class ControlEnricher {
  private static readonly GUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  constructor(
    private fieldService: FieldService,
    private userService: UserService,
    private cropService: CropService
  ) {}

  /**
   * Enrich a single control with additional data based on its type
   */
  async enrichControl(control: TemplateControl): Promise<void> {
    const name = (control.name || '').toLowerCase();
    if (name === 'field') {
      await this.enrichFieldControl(control);
    } else if (name === 'user') {
      await this.enrichUserControl(control);
    } else if (name === 'crop' || name === 'sowing_crop') {
      await this.enrichCropControl(control);
    }
  }

  /**
   * Enrich field control with field name data
   */
  private async enrichFieldControl(control: TemplateControl): Promise<void> {
    const fieldId = control.values_ru || control.values_en || control.values_uz;

    if (!fieldId || fieldId === '' || fieldId === '00000000-0000-0000-0000-000000000000') {
      this.setControlValuesToDefault(control);
      return;
    }

    try {
      const field = await firstValueFrom(
        this.fieldService.cropMonitoringFieldGetByIdIdGet(fieldId)
      );

      if (field) {
        const f: any = field as any;
        control.values_en = (f.name_en as string) || (f.name as string) || '';
        control.values_ru = (f.name_ru as string) || (f.name as string) || '';
        control.values_uz = (f.name_uz as string) || (f.name as string) || '';
      } else {
        this.setControlValuesToDefault(control);
      }
    } catch (error) {
      console.error('Error fetching field:', error);
      this.setControlValuesToDefault(control);
    }
  }

  /**
   * Enrich user control with user name data
   */
  private async enrichUserControl(control: TemplateControl): Promise<void> {
    const userId = control.values_ru || control.values_en || control.values_uz;

    if (!userId || userId === '' || userId === '00000000-0000-0000-0000-000000000000') {
      this.setControlValuesToDefault(control);
      return;
    }

    try {
      const fieldWorker = await firstValueFrom(
        this.userService.identityUserGetByIdIdGet(userId)
      );

      if (fieldWorker) {
        const formattedName = userNames(fieldWorker);
        control.values_en = formattedName;
        control.values_ru = formattedName;
        control.values_uz = formattedName;
      } else {
        this.setControlValuesToDefault(control);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      this.setControlValuesToDefault(control);
    }
  }

  /**
   * Crop id is stored in resultValues (report would otherwise show the raw UUID).
   */
  private async enrichCropControl(control: TemplateControl): Promise<void> {
    const cropId = this.readGuid(control);
    if (!cropId) {
      return;
    }

    try {
      const crop = await firstValueFrom(
        this.cropService.dictionariesCropGetByIdIdGet(cropId)
      );
      const names = this.cropDisplayNames(crop);
      if (!names.en && !names.ru && !names.uz) {
        return;
      }

      control.values_en = names.en || names.ru || names.uz;
      control.values_ru = names.ru || names.en || names.uz;
      control.values_uz = names.uz || names.en || names.ru;
      control.resultValues = '';
    } catch (error) {
      console.error('Error fetching crop:', error);
    }
  }

  private readGuid(control: TemplateControl): string | null {
    const fromResult = typeof control.resultValues === 'string' ? control.resultValues.trim() : '';
    if (this.isGuid(fromResult)) {
      return fromResult;
    }

    for (const candidate of [control.values_ru, control.values_en, control.values_uz]) {
      const text = (candidate || '').trim();
      if (this.isGuid(text)) {
        return text;
      }
    }

    return null;
  }

  private isGuid(value: string): boolean {
    return ControlEnricher.GUID_RE.test(value)
      && value !== '00000000-0000-0000-0000-000000000000';
  }

  private cropDisplayNames(crop: Crop | null | undefined): { en: string; ru: string; uz: string } {
    const names = crop?.names as Record<string, string | null | undefined> | undefined;
    const pick = (...keys: string[]) => {
      if (!names) {
        return '';
      }
      for (const key of keys) {
        const value = names[key];
        if (typeof value === 'string' && value.trim() !== '') {
          return value.trim();
        }
      }
      return '';
    };

    return {
      en: pick('en-US', 'en'),
      ru: pick('ru-RU', 'ru'),
      uz: pick('uz-Latn-UZ', 'uz')
    };
  }

  /**
   * Set control values to default '-' value
   */
  private setControlValuesToDefault(control: TemplateControl): void {
    control.values_en = '-';
    control.values_ru = '-';
    control.values_uz = '-';
  }
}

